import type { MetricDef, MetricFormat, TargetsMap } from '@nono/contract';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, type ReactNode, useMemo, useState } from 'react';
import { MetricInfoTrigger } from '@/components/data/MetricInfo';
import { ACCENT } from '@/lib/chart';
import { formatIsoDate, formatMetric } from '@/lib/format';
import {
  evaluateStatus,
  FAMILY_ACCENT,
  type Family,
  familyOf,
  type MetricStatus,
} from '@/lib/metric-family';
import { cn } from '@/lib/utils';

export type MetricRow = { date: string } & Record<string, number | string | null>;

interface MetricsTableProps {
  /** Columns to render (the caller curates headline vs all). */
  defs: MetricDef[];
  rows: MetricRow[];
  targets: TargetsMap;
  totals?: MetricRow | null;
  dateLabel: string;
  totalsLabel: string;
  /** When set, each day row expands to reveal this content. */
  renderExpansion?: (row: MetricRow) => ReactNode;
}

const STATUS_TEXT: Record<MetricStatus, string> = {
  good: 'text-good-fg',
  warn: 'text-warn-fg',
  bad: 'text-bad-fg',
};
const STATUS_TICK: Record<MetricStatus, string> = {
  good: 'border-good',
  warn: 'border-warn',
  bad: 'border-bad',
};

// The sticky first (date) column. bg classes keep scrolling cells from bleeding
// through; z keeps it above the horizontally-scrolling metric cells. On mobile a
// hairline right divider signals the column is frozen and the rest scrolls.
const STICKY =
  'sticky left-0 z-10 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-border md:after:hidden';

/**
 * The centerpiece grid. Date rows × metric columns, columns banded by funnel
 * family. The Date column is frozen (sticky-left) so it stays visible if the
 * caller shows the full column set and the table scrolls. Status is a 2px tick +
 * tinted number, only on decision metrics with an active target. null → em dash.
 */
export function MetricsTable({
  defs,
  rows,
  targets,
  totals,
  dateLabel,
  totalsLabel,
  renderExpansion,
}: MetricsTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const expandable = Boolean(renderExpansion);

  const groups = useMemo(() => {
    const order: Family[] = [];
    const byFamily = new Map<Family, MetricDef[]>();
    for (const d of defs) {
      const f = familyOf(d.key);
      const list = byFamily.get(f);
      if (list) list.push(d);
      else {
        byFamily.set(f, [d]);
        order.push(f);
      }
    }
    return order.map((f) => ({ family: f, defs: byFamily.get(f) ?? [] }));
  }, [defs]);

  const flatDefs = groups.flatMap((g) => g.defs);
  const totalCols = flatDefs.length + 1;

  function toggle(date: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface shadow-card">
      <table className="w-full border-collapse text-body">
        <thead>
          <tr className="border-b border-border">
            <th className={cn(STICKY, 'z-20 bg-surface-2')} />
            {groups.map((g) => (
              <th
                key={g.family}
                colSpan={g.defs.length}
                className="border-l border-border bg-surface-2 px-2 py-1.5 text-left text-micro font-semibold uppercase tracking-wider sm:px-3"
                style={{ color: ACCENT[FAMILY_ACCENT[g.family]] }}
              >
                {g.family}
              </th>
            ))}
          </tr>
          <tr className="border-b border-border bg-surface-2">
            <th
              className={cn(
                STICKY,
                'z-20 bg-surface-2 px-2 py-2 text-left text-label font-medium text-fg-muted sm:px-3',
              )}
            >
              {dateLabel}
            </th>
            {flatDefs.map((d) => (
              <th
                key={d.key}
                className="whitespace-nowrap px-2 py-2 text-right text-label font-medium text-fg-muted sm:px-3"
              >
                <MetricInfoTrigger metricKey={d.key} label={d.label} />
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((row) => {
            const { weekday, date } = formatIsoDate(row.date);
            const isOpen = expanded.has(row.date);
            return (
              <Fragment key={row.date}>
                <tr className="group border-b border-border/60 transition-fast hover:bg-surface-2/50">
                  <th
                    className={cn(
                      STICKY,
                      'whitespace-nowrap bg-surface px-2 py-2 text-left font-normal transition-fast group-hover:bg-surface-2 sm:px-3',
                      expandable && 'cursor-pointer',
                    )}
                    onClick={expandable ? () => toggle(row.date) : undefined}
                  >
                    <span className="inline-flex items-center gap-1.5">
                      {expandable ? (
                        isOpen ? (
                          <ChevronDown className="size-3.5 text-fg-subtle" aria-hidden />
                        ) : (
                          <ChevronRight className="size-3.5 text-fg-subtle" aria-hidden />
                        )
                      ) : null}
                      <span className="text-fg-subtle">{weekday}</span>
                      <span className="text-fg">{date}</span>
                    </span>
                  </th>
                  {flatDefs.map((d) => (
                    <td key={d.key} className="px-2 py-2 text-right sm:px-3">
                      <Cell
                        value={asNum(row[d.key])}
                        format={d.format}
                        status={
                          d.decision ? evaluateStatus(asNum(row[d.key]), d.key, targets) : null
                        }
                      />
                    </td>
                  ))}
                </tr>
                {expandable && isOpen ? (
                  <tr className="border-b border-border/60">
                    <td colSpan={totalCols} className="bg-surface-2 p-0">
                      {/* Pin the expansion panel to the viewport's left edge regardless of how
                          far the metrics table is scrolled sideways, so the nested ad tree is
                          reachable on mobile. Desktop (lg+) reverts to the natural cell width. */}
                      <div className="sticky left-0 w-screen max-w-[calc(100vw-2rem)] px-2 py-3 sm:px-3 lg:w-auto lg:max-w-none">
                        {renderExpansion?.(row)}
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>

        {totals ? (
          <tfoot>
            <tr className="border-t-2 border-border-strong bg-surface-2 font-medium">
              <th className={cn(STICKY, 'bg-surface-2 px-2 py-2 text-left text-fg sm:px-3')}>
                {totalsLabel}
              </th>
              {flatDefs.map((d) => (
                <td key={d.key} className="px-2 py-2 text-right sm:px-3">
                  <Cell
                    value={asNum(totals[d.key])}
                    format={d.format}
                    status={
                      d.decision ? evaluateStatus(asNum(totals[d.key]), d.key, targets) : null
                    }
                  />
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}

function Cell({
  value,
  format,
  status,
}: {
  value: number | null;
  format: MetricFormat;
  status: MetricStatus | null;
}) {
  return (
    <span
      className={cn(
        'inline-block whitespace-nowrap border-l-2 pl-1.5 tabular',
        status ? `${STATUS_TEXT[status]} ${STATUS_TICK[status]}` : 'border-transparent text-fg',
      )}
    >
      {formatMetric(value, format)}
    </span>
  );
}

function asNum(v: number | string | null | undefined): number | null {
  return typeof v === 'number' ? v : null;
}
